/**
 * Workspaces — create + list hooks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Workspace, WorkspaceSummary, WorkspaceMember } from '@friday/shared/realtime'

export type WorkspaceInviteRole = 'lead' | 'contributor' | 'viewer'

export interface WorkspaceInvite {
  id: string
  workspaceId: string
  email: string
  role: 'admin' | 'member' | 'viewer'
  invitedBy: string
  expiresAt: string
  acceptedAt: string | null
  createdAt: string
}

export interface WorkspaceInviteCreateResponse {
  id: string
  inviteUrl: string
  email: string
  role: WorkspaceInviteRole
  expiresAt: string
}

export function useWorkspaces() {
  return useQuery({
    queryKey: ['workspaces'],
    queryFn: () => api<Workspace[]>('/api/v1/workspaces'),
  })
}

export interface CreateWorkspaceInput {
  name: string
  slug: string
}

export function useCreateWorkspace() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateWorkspaceInput) =>
      api<Workspace>('/api/v1/workspaces', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspaces'] })
      void qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

/** Overview aggregates — projects + task counts + workspace totals. */
export function useWorkspaceOverview(wsId: string | undefined) {
  return useQuery({
    queryKey: ['workspace-overview', wsId],
    queryFn: () => api<WorkspaceSummary>(`/api/v1/workspaces/${wsId}/summary`),
    enabled: !!wsId,
  })
}

export interface ArchiveWorkspaceResult {
  id: string
  archived: true
  cascadedProjects: number
  cascadedTasks: number
}

/** Archive a workspace + cascade-archive its projects and tasks. */
export function useArchiveWorkspace() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (wsId: string) =>
      api<ArchiveWorkspaceResult>(`/api/v1/workspaces/${wsId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      // Refetch synchronously before the caller navigates, so the sidebar
      // already reflects the new state.
      await qc.invalidateQueries({ queryKey: ['workspaces'] })
      await qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

/**
 * Restore a previously-archived workspace. PATCH with `archived: false`
 * clears `archivedAt` on the row. The SPA then re-fetches the workspace
 * list so the sidebar immediately shows the restored workspace.
 */
export function useRestoreWorkspace() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (wsId: string) =>
      api<{ id: string; archived: false }>(`/api/v1/workspaces/${wsId}`, {
        method: 'PATCH',
        body: { archived: false },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['workspaces'] })
      await qc.invalidateQueries({ queryKey: ['me'] })
      await qc.invalidateQueries({ queryKey: ['archive'] })
    },
  })
}

// ────────────── Archive view ──────────────

export interface ArchivedWorkspace {
  id: string
  slug: string
  name: string
  ownerUserId: string
  archivedAt: string | null
  createdAt: string
  version: number
  role: 'owner' | 'admin' | 'member' | 'viewer'
}
export interface ArchivedProject {
  id: string
  workspaceId: string
  key: string
  name: string
  color: string
  archivedAt: string | null
  version: number
  ownerUserId: string
  createdAt: string
}
export interface ArchivedTask {
  id: string
  projectId: string
  workspaceId: string
  projectKey: string
  projectName: string
  key: string
  title: string
  status: string
  priority: string
  archivedAt: string | null
  version: number
}
export interface ArchiveSnapshot {
  workspaces: ArchivedWorkspace[]
  projects: ArchivedProject[]
  tasks: ArchivedTask[]
}

/** List archived workspaces / projects / tasks visible to the current user. */
export function useArchive() {
  return useQuery({
    queryKey: ['archive'],
    queryFn: () => api<ArchiveSnapshot>('/api/v1/me/archive'),
  })
}

// ────────────── Workspace members + invites ──────────────

/** List members of a workspace. */
export function useWorkspaceMembers(wsId: string | undefined) {
  return useQuery({
    queryKey: ['workspace-members', wsId],
    queryFn: () => api<WorkspaceMember[]>(`/api/v1/workspaces/${wsId}/members`),
    enabled: !!wsId,
  })
}

/**
 * List pending workspace invites (admin-only). The endpoint is refused for
 * non-managers, so callers that may be a plain member pass `enabled: false`
 * and the request is never sent.
 */
export function useWorkspaceInvites(wsId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['workspace-invites', wsId],
    queryFn: () => api<WorkspaceInvite[]>(`/api/v1/workspaces/${wsId}/invites`),
    enabled: !!wsId && enabled,
  })
}

/** Create a workspace invite. Caller is responsible for picking the role. */
export function useCreateWorkspaceInvite(wsId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: WorkspaceInviteRole }) =>
      api<WorkspaceInviteCreateResponse>(`/api/v1/workspaces/${wsId}/invites`, {
        method: 'POST',
        body: { email, role },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace-invites', wsId] })
    },
  })
}

/** Revoke a pending workspace invite. */
export function useRevokeWorkspaceInvite(wsId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api(`/api/v1/workspaces/${wsId}/invites/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace-invites', wsId] })
    },
  })
}

/** Change a workspace member's role. */
export function useUpdateWorkspaceMember(wsId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'admin' | 'member' | 'viewer' }) =>
      api(`/api/v1/workspaces/${wsId}/members/${userId}`, { method: 'PATCH', body: { role } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace-members', wsId] })
    },
  })
}

/** Remove a workspace member. */
export function useRemoveWorkspaceMember(wsId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      api(`/api/v1/workspaces/${wsId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workspace-members', wsId] })
    },
  })
}